import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';

// Custom hooks
import { useKasaBankData } from '../hooks/useKasaBankData';
import { useOperationFilters } from '../hooks/useOperationFilters';
import { useOperationExport } from '../hooks/useOperationExport';

// Components
import KasaBankHeader from '../components/kasabank/KasaBankHeader';
import SaldoCards from '../components/kasabank/SaldoCards';
import DailyMonthlySummary from '../components/kasabank/DailyMonthlySummary';
import OverviewTab from '../components/kasabank/OverviewTab';
import PaymentsTab from '../components/kasabank/PaymentsTab';
import AllOperationsTab from '../components/kasabank/AllOperationsTab';
import PlatnosciZmianyTab from '../components/kasabank/PlatnosciZmianyTab';
import DocumentsTable from '../components/kasabank/DocumentsTable';
import AddOperationModal from '../components/kasabank/AddOperationModal';
import EditOperationModal from '../components/kasabank/EditOperationModal';

// Constants
import { TABS } from '../components/kasabank/kasaBankHelpers';

const KasaBankPage = () => {
  const { selectedLocation, locationId } = useLocation();
  const { hasPermission } = usePermissions();

  // Permissions
  const canAccessKasaBank = hasPermission('kasa_bank', 'dostep', locationId);
  const canViewOperations = hasPermission('kasa_bank', 'podglad', locationId);
  const canManageOperations = hasPermission('kasa_bank', 'operacje', locationId);
  const canEditOperations = hasPermission('kasa_bank', 'edycja', locationId);

  // Server-side filters / pagination state for "wszystkie operacje"
  const [dateFilters, setDateFilters] = useState({ startDate: '', endDate: '' });
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);

  // Server-side filters / pagination state for KP
  const [kpDateFilters, setKpDateFilters] = useState({ startDate: '', endDate: '' });
  const [kpPage, setKpPage] = useState(1);
  const [kpPageLimit, setKpPageLimit] = useState(50);

  // Server-side filters / pagination state for KW
  const [kwDateFilters, setKwDateFilters] = useState({ startDate: '', endDate: '' });
  const [kwPage, setKwPage] = useState(1);
  const [kwPageLimit, setKwPageLimit] = useState(50);

  // Reset paginacji przy zmianie filtrow/limitu
  useEffect(() => { setPage(1); }, [dateFilters.startDate, dateFilters.endDate, pageLimit]);
  useEffect(() => { setKpPage(1); }, [kpDateFilters.startDate, kpDateFilters.endDate, kpPageLimit]);
  useEffect(() => { setKwPage(1); }, [kwDateFilters.startDate, kwDateFilters.endDate, kwPageLimit]);

  const operacjeQuery = useMemo(() => ({
    limit: pageLimit,
    offset: (page - 1) * pageLimit,
    dateFrom: dateFilters.startDate || undefined,
    dateTo: dateFilters.endDate || undefined
  }), [page, pageLimit, dateFilters.startDate, dateFilters.endDate]);

  const kpQuery = useMemo(() => ({
    limit: kpPageLimit,
    offset: (kpPage - 1) * kpPageLimit,
    dateFrom: kpDateFilters.startDate || undefined,
    dateTo: kpDateFilters.endDate || undefined
  }), [kpPage, kpPageLimit, kpDateFilters.startDate, kpDateFilters.endDate]);

  const kwQuery = useMemo(() => ({
    limit: kwPageLimit,
    offset: (kwPage - 1) * kwPageLimit,
    dateFrom: kwDateFilters.startDate || undefined,
    dateTo: kwDateFilters.endDate || undefined
  }), [kwPage, kwPageLimit, kwDateFilters.startDate, kwDateFilters.endDate]);

  // Data hook
  const {
    saldo, operacje, operacjeTotal, operacjeLoading,
    dailySummary, monthlyStats, paymentsByType,
    kpDocuments, kpDocumentsTotal, kpDocumentsLoading,
    kwDocuments, kwDocumentsTotal, kwDocumentsLoading,
    loading, error, setError,
    loadData
  } = useKasaBankData(selectedLocation, operacjeQuery, kpQuery, kwQuery);

  // Filter / selection hook (only client-side payment filters + selection)
  const {
    paymentFilters,
    filteredOperations, selectedOperations, selectAll,
    togglePaymentFilter, toggleAllFilters,
    toggleOperationSelection, toggleSelectAll
  } = useOperationFilters(operacje);

  // Export hook
  const { exportToCSV, exportToPDF } = useOperationExport(
    filteredOperations, selectedOperations, dateFilters
  );

  // Local UI state
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddKPModal, setShowAddKPModal] = useState(false);
  const [showAddKWModal, setShowAddKWModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState(null);

  // ---------- Early returns ----------

  if (!selectedLocation) {
    return (
      <div style={{
        width: '100%',
        padding: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{
          textAlign: 'center', backgroundColor: 'var(--card-bg)', padding: '3rem',
          borderRadius: '1rem', boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.1)',
          border: '1px solid var(--border)'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <i className="fas fa-map-marker-alt" style={{ fontSize: '3rem', color: 'var(--warning)' }}></i>
          </div>
          <h4 style={{ color: 'var(--text)', marginBottom: '0.5rem' }}>Wybierz lokalizację</h4>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>
            Aby wyświetlić dane Kasa-Bank, wybierz lokalizację z menu powyżej.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Ładowanie...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!canAccessKasaBank && !canViewOperations) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{
          backgroundColor: 'var(--warning-light)', color: 'var(--warning)', padding: '1.5rem',
          borderRadius: '8px', border: '1px solid var(--warning)', maxWidth: '500px', margin: '2rem auto'
        }}>
          <h5 style={{ marginBottom: '0.5rem' }}>🔒 Brak dostępu</h5>
          <p style={{ margin: 0, fontSize: '13px' }}>
            Nie masz uprawnień do modułu Kasa / Bank. Skontaktuj się z administratorem.
          </p>
        </div>
      </div>
    );
  }

  // ---------- Handlers ----------

  const handleEditOperation = (operacja) => {
    setSelectedOperation(operacja);
    setShowEditModal(true);
  };

  // ---------- Render ----------

  return (
    <div style={{ width: '100%', padding: '1.5rem' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <KasaBankHeader
          selectedLocation={selectedLocation}
          loading={loading}
          onRefresh={loadData}
        />

        {/* Error alert */}
        {error && (
          <div style={{
            padding: '0.75rem 1rem', marginBottom: '0.6rem',
            backgroundColor: 'var(--danger-light)', border: '1px solid var(--danger-light)',
            borderRadius: '0.375rem', color: 'var(--danger)',
            display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            <i className="fas fa-exclamation-circle"></i>
            {error}
            <button
              onClick={() => setError('')}
              style={{
                marginLeft: 'auto', background: 'none', 
                color: 'var(--danger)', cursor: 'pointer', fontSize: '0.85rem'
              }}
            >
              ×
            </button>
          </div>
        )}

        <SaldoCards saldo={saldo} />
        <DailyMonthlySummary dailySummary={dailySummary} monthlyStats={monthlyStats} />

        {/* Tabs container */}
        <div style={{
          backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
          borderRadius: '0.5rem', overflow: 'hidden',
          boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
        }}>
          {/* Tab navigation */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', backgroundColor: 'var(--bg)' }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '6px 12px',
                  // Standard V3SYS: aktywna zakladka = pelne tlo primary + bialy tekst
                  backgroundColor: activeTab === tab.id ? 'var(--primary)' : 'transparent',
                  color: activeTab === tab.id ? '#fff' : 'var(--text-muted)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  fontSize: '12px', transition: 'all 0.15s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.color = 'var(--text-secondary)';
                    e.target.style.backgroundColor = 'var(--bg)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.color = 'var(--text-muted)';
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <i className={tab.icon} style={{ fontSize: '0.875rem' }}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ padding: '1.5rem' }}>
            {activeTab === 'overview' && <OverviewTab operacje={operacje} />}

            {activeTab === 'payments' && (
              <PaymentsTab
                paymentsByType={paymentsByType}
                dailySummary={dailySummary}
                monthlyStats={monthlyStats}
              />
            )}

            {activeTab === 'all-operations' && (
              <AllOperationsTab
                filteredOperations={filteredOperations}
                selectedOperations={selectedOperations}
                selectAll={selectAll}
                paymentFilters={paymentFilters}
                dateFilters={dateFilters}
                setDateFilters={setDateFilters}
                togglePaymentFilter={togglePaymentFilter}
                toggleAllFilters={toggleAllFilters}
                toggleOperationSelection={toggleOperationSelection}
                toggleSelectAll={toggleSelectAll}
                exportToCSV={exportToCSV}
                exportToPDF={exportToPDF}
                canEditOperations={canEditOperations}
                onEditOperation={handleEditOperation}
                page={page}
                pageLimit={pageLimit}
                totalCount={operacjeTotal}
                operacjeLoading={operacjeLoading}
                onPageChange={setPage}
                onLimitChange={setPageLimit}
              />
            )}

            {activeTab === 'platnosci-zmiany' && (
              <PlatnosciZmianyTab
                locationId={locationId}
                onZmieniono={loadData}
              />
            )}

            {activeTab === 'kp' && (
              <DocumentsTable
                type="KP"
                documents={kpDocuments}
                canManage={canManageOperations}
                onAddClick={() => setShowAddKPModal(true)}
                dateFilters={kpDateFilters}
                setDateFilters={setKpDateFilters}
                page={kpPage}
                pageLimit={kpPageLimit}
                totalCount={kpDocumentsTotal}
                onPageChange={setKpPage}
                onLimitChange={setKpPageLimit}
                isLoading={kpDocumentsLoading}
              />
            )}

            {activeTab === 'kw' && (
              <DocumentsTable
                type="KW"
                documents={kwDocuments}
                canManage={canManageOperations}
                onAddClick={() => setShowAddKWModal(true)}
                dateFilters={kwDateFilters}
                setDateFilters={setKwDateFilters}
                page={kwPage}
                pageLimit={kwPageLimit}
                totalCount={kwDocumentsTotal}
                onPageChange={setKwPage}
                onLimitChange={setKwPageLimit}
                isLoading={kwDocumentsLoading}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddOperationModal
        isOpen={showAddKPModal}
        onClose={() => setShowAddKPModal(false)}
        onSuccess={() => { setShowAddKPModal(false); loadData(); }}
        operationType="KP"
        locationId={locationId}
      />

      <AddOperationModal
        isOpen={showAddKWModal}
        onClose={() => setShowAddKWModal(false)}
        onSuccess={() => { setShowAddKWModal(false); loadData(); }}
        operationType="KW"
        locationId={locationId}
      />

      <EditOperationModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedOperation(null); }}
        onSuccess={() => { setShowEditModal(false); setSelectedOperation(null); loadData(); }}
        operation={selectedOperation}
      />
    </div>
  );
};

export default KasaBankPage;
